import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Appels authentifiés vers l'API Skipya. */
export const useApi = () => {
  const { token } = useAuth();
  // Objet stable tant que le jeton ne change pas : les pages s'en servent comme dépendance d'effet.
  return useMemo(() => {
    const headers = { Authorization: `Bearer ${token}` };
    return {
      get: (path) => axios.get(`${API}${path}`, { headers }).then((r) => r.data),
      post: (path, body) => axios.post(`${API}${path}`, body, { headers }).then((r) => r.data),
      patch: (path, body) => axios.patch(`${API}${path}`, body, { headers }).then((r) => r.data),
      upload: (file, docType) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('doc_type', docType);
        return axios.post(`${API}/documents/upload`, fd, { headers }).then((r) => r.data);
      },
    };
  }, [token]);
};

export const useTone = () => {
  const { isDark } = useTheme();
  return {
    isDark,
    page: isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#F8F7F4] text-[#1d1d1f]',
    panel: isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900',
    muted: isDark ? 'text-[#94A3B8]' : 'text-gray-500',
    soft: isDark ? 'bg-white/5' : 'bg-gray-50',
    input: isDark
      ? 'bg-white/5 border-white/15 text-white placeholder:text-white/40'
      : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400',
  };
};

export const PageShell = ({ title, subtitle, back = '/dashboard', backLabel = 'Mon espace', children, testId }) => {
  const tone = useTone();
  return (
    <div className={`min-h-screen px-4 py-6 md:py-10 ${tone.page}`} data-testid={testId}>
      <div className="max-w-4xl mx-auto">
        <Link to={back} className={`inline-flex items-center gap-2 text-sm mb-6 ${tone.muted} hover:text-[#81d8d0]`}>
          <ArrowLeft size={16} /> {backLabel}
        </Link>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2">{title}</h1>
        {subtitle && <p className={`text-base leading-7 mb-8 ${tone.muted}`}>{subtitle}</p>}
        {children}
      </div>
    </div>
  );
};

export const Panel = ({ title, children, className = '', right, testId }) => {
  const tone = useTone();
  return (
    <section className={`border rounded-2xl p-5 mb-5 ${tone.panel} ${className}`} data-testid={testId}>
      {(title || right) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          {title && <h2 className="text-lg font-semibold">{title}</h2>}
          {right}
        </div>
      )}
      {children}
    </section>
  );
};

export const ProgressBar = ({ value, className = '' }) => {
  const tone = useTone();
  return (
    <div className={`h-2 rounded-full overflow-hidden ${tone.isDark ? 'bg-white/10' : 'bg-gray-100'} ${className}`}>
      <div className="h-full bg-[#81d8d0] transition-all duration-500" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
};

export const STATUS_LABELS = {
  en_preparation: 'En préparation',
  pret: 'Prêt à déposer',
  depose: 'Déposé, en attente de réponse',
  accepte: 'Accepté',
  refuse: 'Refusé',
  abandonne: 'Abandonné',
};

export const StatusBadge = ({ status }) => {
  const color = {
    en_preparation: 'bg-amber-100 text-amber-800',
    pret: 'bg-[#81d8d0]/25 text-[#0b6b63]',
    depose: 'bg-blue-100 text-blue-800',
    accepte: 'bg-emerald-100 text-emerald-800',
    refuse: 'bg-rose-100 text-rose-800',
    abandonne: 'bg-gray-100 text-gray-600',
  }[status] || 'bg-gray-100 text-gray-600';
  return <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${color}`}>{STATUS_LABELS[status] || status}</span>;
};

export const primaryButton = 'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#81d8d0] text-[#0F0F1A] text-sm font-semibold hover:bg-[#6fcac2] disabled:opacity-40 disabled:cursor-not-allowed';
export const secondaryButton = 'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-[#81d8d0] text-sm font-semibold hover:bg-[#81d8d0]/10 disabled:opacity-40';

/** Champ du profil universel, rendu selon sa spécification serveur. */
export const FieldInput = ({ field, value, onChange }) => {
  const tone = useTone();
  const base = `w-full rounded-xl border px-3 py-2.5 text-sm ${tone.input}`;
  const id = `field-${field.key}`;
  let control;
  if (field.type === 'select') {
    control = (
      <select id={id} className={base} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">Choisir…</option>
        {field.options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
    );
  } else if (field.type === 'bool') {
    control = (
      <div className="flex gap-2" role="radiogroup" aria-labelledby={id}>
        {[[true, 'Oui'], [false, 'Non']].map(([v, label]) => (
          <button
            key={label}
            type="button"
            onClick={() => onChange(v)}
            className={`px-4 py-2 rounded-xl border text-sm ${value === v ? 'border-[#81d8d0] bg-[#81d8d0]/15 font-semibold' : tone.input}`}
          >
            {label}
          </button>
        ))}
      </div>
    );
  } else {
    control = (
      <input
        id={id}
        className={base}
        type={field.type === 'date' ? 'date' : 'text'}
        inputMode={field.type === 'number' ? 'decimal' : undefined}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <label className="block" htmlFor={id}>
      <span id={field.type === 'bool' ? id : undefined} className="block text-sm font-medium mb-1.5">{field.label}</span>
      {control}
    </label>
  );
};
